import type { PayloadAction } from '@reduxjs/toolkit';
import { INITIAL_META } from 'src/components/VendorPortal/ListingLayout/shared/constants';
import type { FilterMap, GenericObj } from 'src/helper/shared-types';
import { REQUEST_SORT_KEYS } from 'src/views/VendorPortal/Request/constants';
import {
  CLAIM_REQUEST_SCREEN_KEYS,
  type ClaimRequestKey,
} from 'src/views/VendorPortal/Request/SidePanel/ClaimPanel/constants';
import {
  PASS_REQUEST_SCREEN_KEYS,
  type PassRequestKey,
} from 'src/views/VendorPortal/Request/SidePanel/PassPanel/constants';
import type {
  RequestDataType,
  RequestDetailsSidePanelType,
  RequestIssueType,
  RequestStateType,
} from './types';

const reducers = {
  clearRequestListFilters: (state: RequestStateType) => {
    state.filters = {
      category: [''],
      status: ['claim_pending'],
    };

    state.isFilteringList = true;
    state.meta = INITIAL_META;
    state.filtersCount = 0;
    state.search.searchInput = '';
  },
  filterRequestList: (
    state: GenericObj & RequestStateType,
    action: PayloadAction<{
      key: string;
      count: number;
      value: string[];
    }>,
  ) => {
    const { key, count, value } = action.payload;

    if (state.filters[key] !== value) {
      state.filtersCount = count;
      state.isFilteringList = true;
      state.filters = {
        ...state.filters,
        [key]: value,
      };
      state.meta = INITIAL_META;
    }
  },
  searchRequestList: (state: RequestStateType, action: PayloadAction<string>) => {
    if (state.search.searchInput !== action.payload) {
      state.isFilteringList = true;
      state.meta = INITIAL_META;
      state.search.searchInput = action.payload;
    }
  },
  setRequestFilterMapping: (state: RequestStateType, action: PayloadAction<FilterMap[]>) => {
    state.filterMapping = action.payload;
  },
  sortRequestList: (state: RequestStateType, action: PayloadAction<string>) => {
    // Only set loading state if the value actually changed
    if (state.sortBy !== action.payload) {
      state.isFilteringList = true;
    }
    state.sortBy = action.payload;
    state.meta = INITIAL_META;
  },
  removeItemFromRequestList: (state: RequestStateType, action: PayloadAction<string>) => {
    state.data = state.data.filter((item) => item.work_order_id !== action.payload);
  },
  openRequestDetailsSidePanel: (
    state: RequestStateType,
    action: PayloadAction<RequestDetailsSidePanelType['workOrderUuid']>,
  ) => {
    // ORI-459: a hardware Back that dismisses a nested sub-panel re-fires the
    // ?reqId popstate listener (RequestDetailsPanel), which re-dispatches this
    // open for the SAME work order. Re-opening an already-open panel must be a
    // no-op — flipping isLoading would flash a skeleton over the still-visible
    // panel and refetch for nothing.
    if (
      state.sidePanels.requestDetails.isOpen &&
      state.sidePanels.requestDetails.workOrderUuid === action.payload
    ) {
      return;
    }
    // ORI-627: drop the previous request's details before loading a different one.
    // `source` had exactly one writer (getRequestDetails().fulfilled) and was never
    // cleared, which stayed invisible while a failed load closed the panel outright. Now
    // that a transient failure keeps the panel open, an uncleared `source` would render
    // the PREVIOUS request's NTE, issues and address under the new request's id — and
    // seed Claim / Pass / Request-Availability from it.
    //
    // Deliberately `{}` and NOT a shaped literal: this is byte-identical to the slice's
    // initial `source`, which the whole Request subtree already renders against during
    // every in-flight details GET. Seeding e.g. `resident_availabilities: {}` for
    // symmetry with the work-order slice would defeat the `?.` short-circuit in
    // useRequestDetails and crash on `.availability.length`.
    state.source = {} as RequestDataType;
    state.sidePanels.requestDetails = {
      ...state.sidePanels.requestDetails,
      isOpen: true,
      isLoading: true,
      workOrderUuid: action.payload,
      // A newly-opened request carries no failure from the last one.
      hasDetailsError: false,
    };
  },
  // ORI-627: re-arm the details fetch after a failed load, behind the panel's inline
  // Retry. Rides `needToRefetch` — the same channel the (still-TODO) request Pusher hook
  // will use — so the refetch effect's abort-before-create handling covers both.
  retryRequestDetails: (state: RequestStateType) => {
    state.sidePanels.requestDetails = {
      ...state.sidePanels.requestDetails,
      needToRefetch: true,
    };
  },
  closeRequestDetailsSidePanel: (state: RequestStateType) => {
    state.sidePanels.requestDetails = {
      ...state.sidePanels.requestDetails,
      isOpen: false,
      workOrderUuid: '',
      // ORI-627: a closed panel has no failure to report; leaving the flag set would
      // resurface the error state the next time this panel opens.
      hasDetailsError: false,
    };
    // ORI-459 cascade-close: tearing down the parent also resets every nested
    // sub-panel, so a Back past the parent can never orphan a visible nested
    // panel over a closed parent. Pure state reset — history reconciliation is
    // owned by useHistoryDismiss in the components.
    // (claimRequest.claimingWorkOrderUuid is intentionally preserved: ORI-420
    // eviction must survive a mid-flight panel close.)
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.passRequest = {
      ...state.sidePanels.passRequest,
      currentScreen: PASS_REQUEST_SCREEN_KEYS.SELECT_REASON,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.requestAvailability = {
      ...state.sidePanels.requestAvailability,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.claimRequest = {
      ...state.sidePanels.claimRequest,
      currentScreen: CLAIM_REQUEST_SCREEN_KEYS.REVIEW_AND_CONFIRM,
      data: undefined,
      isOpen: false,
    };
  },
  openRequestIssueDetailsSidePanel: (
    state: RequestStateType,
    action: PayloadAction<RequestIssueType>,
  ) => {
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      data: action.payload,
      isOpen: true,
      isLoading: true,
    };
  },
  closeRequestIssueDetailsSidePanel: (state: RequestStateType) => {
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      data: undefined,
      isOpen: false,
    };
  },
  openPassRequestSidePanel: (state: RequestStateType) => {
    state.sidePanels.passRequest = {
      ...state.sidePanels.passRequest,
      currentScreen: PASS_REQUEST_SCREEN_KEYS.SELECT_REASON,
      data: {
        reason: '',
        notes: '',
      },
      isOpen: true,
      isLoading: true,
    };
  },
  closePassRequestSidePanel: (state: RequestStateType) => {
    state.sidePanels.passRequest = {
      ...state.sidePanels.passRequest,
      data: undefined,
      isOpen: false,
    };
  },
  updatePassRequestTabSidePanel: (
    state: RequestStateType,
    action: PayloadAction<PassRequestKey>,
  ) => {
    state.sidePanels.passRequest.currentScreen = action.payload;
  },
  openRequestAvailabilitySidePanel: (
    state: RequestStateType,
    action: PayloadAction<{
      work_order_id: string;
    }>,
  ) => {
    state.sidePanels.requestAvailability = {
      ...state.sidePanels.requestAvailability,
      data: action.payload,
      isOpen: true,
      isPending: true,
    };
  },
  closeRequestAvailabilitySidePanel: (state: RequestStateType) => {
    state.sidePanels.requestAvailability = {
      ...state.sidePanels.requestAvailability,
      data: undefined,
      isOpen: false,
    };
  },
  openClaimRequestSidePanel: (state: RequestStateType) => {
    // ORI-627 follow-up: refuse to open Claim unless `source` is the request the panel is
    // actually showing. The seed below reads the store at dispatch time, so a view-level
    // guard alone is positional — it protects the one button that exists today and nothing
    // else. Refusing here makes it structural: a future entry point cannot dispatch its way
    // into acknowledging another request's NTE (or a `0` standing in for "not loaded yet").
    // Same identity test as selectIsVendorRequestLoaded; when loaded this is a no-op.
    const loadedId = state.source?.work_order_id;
    if (!loadedId || loadedId !== state.sidePanels.requestDetails.workOrderUuid) {
      return;
    }

    state.sidePanels.claimRequest = {
      ...state.sidePanels.claimRequest,
      currentScreen: CLAIM_REQUEST_SCREEN_KEYS.REVIEW_AND_CONFIRM,
      data: {
        service_window_reference_id: '',
        nte_accepted: false,
        nte_amount_in_cents: state.source.nte_amount_in_cents || 0,
      },
      isOpen: true,
      isLoading: true,
    };
  },
  closeClaimRequestSidePanel: (state: RequestStateType) => {
    state.sidePanels.claimRequest = {
      ...state.sidePanels.claimRequest,
      data: undefined,
      isOpen: false,
    };
  },
  updateClaimRequestTabSidePanel: (
    state: RequestStateType,
    action: PayloadAction<ClaimRequestKey>,
  ) => {
    state.sidePanels.claimRequest.currentScreen = action.payload;
  },
  updateRequestViewedInList: (
    state: RequestStateType,
    action: PayloadAction<{
      workOrderId: string;
    }>,
  ) => {
    const { workOrderId } = action.payload;
    const workOrderIndex = state.data.findIndex(
      (workOrder) => workOrder.work_order_id === workOrderId,
    );

    if (workOrderIndex !== -1) {
      state.data[workOrderIndex].isViewed = true;
    }
  },
  resetRequestListState: (state: RequestStateType) => {
    state.search.searchInput = '';
    state.data = [];
    state.meta = INITIAL_META;
    state.isFilteringList = false;
    state.filtersCount = 0;
    state.filters = {
      category: [''],
      status: ['claim_pending'],
    };
    state.sortBy = `-${REQUEST_SORT_KEYS.RECEIVED}`;
  },
};
export default reducers;
